<script setup lang="ts">
import { FileText, BarChart3 } from 'lucide-vue-next';
import SceneShell from '../SceneShell.vue';

const cells = Array.from({ length: 9 }, (_, i) => i);
</script>

<template>
    <SceneShell>
        <div
            class="pointer-events-none absolute right-1/4 -bottom-16 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl"
        ></div>

        <!-- Source document with scan beam -->
        <div class="endpoint top-1/2 left-[20%]">
            <div class="doc" aria-hidden="true">
                <FileText class="doc-watermark" />
                <span class="doc-line"></span>
                <span class="doc-line w-2/3"></span>
                <span class="doc-line"></span>
                <span class="doc-line w-1/2"></span>
                <span class="scan-beam"></span>
            </div>
            <p class="endpoint-label text-slate-300">Texte intégral</p>
        </div>

        <!-- Data particles -->
        <span
            v-for="p in 3"
            :key="p"
            class="particle"
            :style="{ '--d': `${(p - 1) * -0.9}s` }"
        ></span>

        <!-- Extraction grid -->
        <div class="endpoint top-1/2 left-[72%]">
            <div class="grid-box" aria-hidden="true">
                <span
                    v-for="c in cells"
                    :key="c"
                    class="cell"
                    :style="{ '--cd': `${c * 0.25}s` }"
                ></span>
            </div>
            <p
                class="endpoint-label flex items-center justify-center gap-1 text-cyan-200"
            >
                <BarChart3 class="h-3 w-3" />
                Grille &amp; PRISMA
            </p>
        </div>

        <p class="caption">
            L'IA pré-remplit, vous vérifiez — le tableau se construit.
        </p>
    </SceneShell>
</template>

<style scoped>
.endpoint {
    position: absolute;
    z-index: 10;
    transform: translate(-50%, -50%);
    text-align: center;
}

.endpoint-label {
    margin-top: 0.45rem;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.doc {
    position: relative;
    display: flex;
    height: 4.6rem;
    width: 3.4rem;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid rgba(148, 163, 184, 0.3);
    background: #0f172a;
    padding: 8px;
}

.doc-watermark {
    position: absolute;
    top: 5px;
    right: 5px;
    height: 11px;
    width: 11px;
    color: rgba(148, 163, 184, 0.3);
}

.doc-line {
    height: 2px;
    width: 100%;
    border-radius: 9999px;
    background: rgba(148, 163, 184, 0.45);
}

.scan-beam {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 10px;
    background: linear-gradient(
        to bottom,
        transparent,
        rgba(34, 211, 238, 0.35),
        transparent
    );
    opacity: 0;
}

.grid-box {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    width: 5rem;
    padding: 5px;
    border-radius: 8px;
    border: 1px solid rgba(56, 189, 248, 0.35);
    background: rgba(15, 23, 42, 0.7);
}

.cell {
    aspect-ratio: 1.4 / 1;
    border-radius: 2px;
    background: rgba(148, 163, 184, 0.12);
}

.particle {
    position: absolute;
    left: 28%;
    top: 50%;
    z-index: 9;
    height: 6px;
    width: 6px;
    border-radius: 9999px;
    background: rgb(34 211 238);
    box-shadow: 0 0 8px rgba(34, 211, 238, 0.8);
    opacity: 0;
    transform: translate(-50%, -50%);
}

.caption {
    position: absolute;
    bottom: 0.75rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
    color: rgb(100 116 139);
}

/* reduced-motion: show a filled grid */
.cell {
    background: rgba(34, 211, 238, 0.18);
}

@media (prefers-reduced-motion: no-preference) {
    .cell {
        background: rgba(148, 163, 184, 0.12);
        animation: cell-fill 2.4s ease-in-out infinite;
        animation-delay: var(--cd);
    }
    .scan-beam {
        animation: scan-beam 2.4s ease-in-out infinite;
    }
    .particle {
        animation: particle 2.7s ease-in infinite;
        animation-delay: var(--d);
    }

    @keyframes cell-fill {
        0%,
        10% {
            background: rgba(148, 163, 184, 0.12);
        }
        45%,
        85% {
            background: rgba(34, 211, 238, 0.22);
        }
        100% {
            background: rgba(148, 163, 184, 0.12);
        }
    }

    @keyframes scan-beam {
        0% {
            top: -10px;
            opacity: 0;
        }
        20% {
            opacity: 1;
        }
        80% {
            opacity: 1;
        }
        100% {
            top: 100%;
            opacity: 0;
        }
    }

    @keyframes particle {
        0% {
            left: 28%;
            top: 50%;
            opacity: 0;
        }
        15% {
            opacity: 1;
        }
        70% {
            left: 70%;
            top: 50%;
            opacity: 1;
        }
        85%,
        100% {
            left: 72%;
            top: 50%;
            opacity: 0;
        }
    }
}
</style>
